import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Archive,
  ArrowLeft,
  BellOff,
  BellRing,
  Ban,
  CalendarClock,
  CheckCheck,
  CircleCheck,
  CirclePause,
  Flag,
  Inbox,
  Loader,
  MapPin,
  MapPinPlus,
  Play,
  Star,
  TriangleAlert,
  Waypoints,
  type LucideIcon,
} from 'lucide-react';
import type { AvisoMaraDetalle, Detencion, TipoEventoEjecucion, TipoHito, ViajeDetalle } from '@tms/contratos';
import { api } from '../api';
import { ChipEstado } from '../components/ChipEstado';
import { useAccionesAlerta } from '../components/DialogoJustificar';
import { FilaAlerta } from '../components/FilaAlerta';
import { LineaTiempoParadas } from '../components/LineaTiempoParadas';
import { Mapa, type Encuadre, type LineaMapa, type MarcadorUnidad, type PuntoMapa } from '../components/Mapa';
import { MOTIVO_NO_PLANIFICADA, PARADA, UNIDAD } from '../components/estados';
import { useDatos } from '../state/Datos';
import { useSesion } from '../state/Sesion';
import { useToast } from '../state/Toast';
import { compararPorNivel } from '../util/alertas';
import { fmtCoordenadas, fmtDuracionMin, fmtFechaHora, fmtHora, fmtKm, fmtNum, fmtPct } from '../util/format';
import { useAhora, useCarga } from '../util/hooks';
import { puedeEditarRutas } from '../util/roles';

type Pestania = 'detenciones' | 'alertas' | 'avisos' | 'eventos';

const ICONO_HITO: Record<TipoHito, LucideIcon> = {
  PLAN_RECIBIDO: CalendarClock,
  PLAN_MODIFICADO: CalendarClock,
  INICIO: Play,
  PARADA_DETECTADA: MapPin,
  PARADA_DECLARADA: CircleCheck,
  PARADA_NO_PLANIFICADA: MapPinPlus,
  DETENCION: CirclePause,
  ALERTA: BellRing,
  FIN: CheckCheck,
  CANCELADO: Ban,
};

const ETIQUETA_HITO: Record<TipoHito, string> = {
  PLAN_RECIBIDO: 'Plan recibido',
  PLAN_MODIFICADO: 'Plan modificado',
  INICIO: 'Inicio',
  PARADA_DETECTADA: 'Parada detectada',
  PARADA_DECLARADA: 'Parada declarada',
  PARADA_NO_PLANIFICADA: 'Parada no planificada',
  DETENCION: 'Detención',
  ALERTA: 'Alerta',
  FIN: 'Fin',
  CANCELADO: 'Cancelado',
};

const ETIQUETA_AVISO: Record<TipoEventoEjecucion, { etiqueta: string; icono: LucideIcon }> = {
  'viaje.iniciado': { etiqueta: 'Inicio del viaje', icono: Play },
  'parada.registrada': { etiqueta: 'Parada registrada', icono: MapPin },
  'viaje.finalizado': { etiqueta: 'Fin del viaje', icono: Flag },
};

function tipoDetencion(d: Detencion): { etiqueta: string; color: string; icono: LucideIcon; variante: string } {
  if (d.fueraDePlan) return { etiqueta: 'Fuera de plan', color: 'amber', icono: TriangleAlert, variante: 'det-fuera' };
  if (d.paradaId !== null) return { etiqueta: 'En parada de la ruta', color: 'green', icono: MapPin, variante: 'det-parada' };
  if (d.paradaNoPlanificadaId !== null) return { etiqueta: 'Parada no planificada', color: 'brand', icono: MapPinPlus, variante: 'det-np' };
  return { etiqueta: 'Sin clasificar', color: 'muted', icono: CirclePause, variante: 'det-otra' };
}

/** Resumen legible del cuerpo de un aviso de Mara. */
function describirAviso(a: AvisoMaraDetalle, d: ViajeDetalle): string {
  const c = a.cuerpo;
  switch (c.tipo) {
    case 'viaje.iniciado': {
      const chofer = d.choferes.find((x) => x.idMara === c.choferIdMara)?.nombre ?? c.choferIdMara;
      return [chofer ? `Chofer: ${chofer}` : 'Chofer principal', c.kmInicial != null ? `odómetro ${fmtNum(c.kmInicial)} km` : null].filter(Boolean).join(' · ');
    }
    case 'parada.registrada': {
      const p = c.parada;
      if (p.planificada) {
        const parada = d.paradas.find((x) => x.id === p.paradaId);
        return [
          parada ? `${parada.orden}. ${parada.nombre}` : `Parada ${p.paradaId}`,
          p.cantidadEntregada != null ? `entregado ${fmtNum(p.cantidadEntregada)} ${p.unidadMedida ?? ''}`.trim() : null,
          p.observacion ? `«${p.observacion}»` : null,
        ]
          .filter(Boolean)
          .join(' · ');
      }
      return [`No planificada: ${MOTIVO_NO_PLANIFICADA[p.motivo].toLocaleLowerCase('es')}`, p.observacion ? `«${p.observacion}»` : null].filter(Boolean).join(' · ');
    }
    case 'viaje.finalizado':
      return c.kmFinal != null ? `Odómetro ${fmtNum(c.kmFinal)} km` : 'Sin odómetro informado';
  }
}

function Dato({ t, children }: { t: string; children: ReactNode }) {
  return (
    <div>
      <dt>{t}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Ruta asignada al viaje y, si está PLANIFICADO, selector para cambiarla (PUT /viajes/:id/ruta). */
function RutaAsignada({ d, onCambiada }: { d: ViajeDetalle; onCambiada: () => void }) {
  const { usuario } = useSesion();
  const { avisar } = useToast();
  const editable = d.estado === 'PLANIFICADO' && puedeEditarRutas(usuario?.rol);
  const troncal = useCarga(editable ? () => api.troncal(d.troncal.id) : null, [d.troncal.id, editable]);
  const activas = (troncal.datos?.rutas ?? []).filter((r) => r.activa);
  const [elegida, setElegida] = useState<number | ''>('');
  const [guardando, setGuardando] = useState(false);

  const cambiar = async () => {
    if (elegida === '') return;
    setGuardando(true);
    try {
      await api.cambiarRutaViaje(d.id, elegida);
      avisar('Ruta del viaje actualizada');
      setElegida('');
      onCambiada();
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'No se pudo cambiar la ruta.', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const r = d.ruta;
  return (
    <section className="card" aria-label="Ruta asignada">
      <header>
        <h2>
          <Waypoints aria-hidden />
          Ruta asignada
        </h2>
        <Link className="text-link" to={`/troncales/${d.troncal.id}`}>
          Troncal {d.troncal.nombre}
        </Link>
      </header>
      <div className="body ruta-asignada">
        {r ? (
          <div className="ruta-dato">
            <strong>{r.nombre}</strong>
            {r.porDefecto && (
              <span className="chip green">
                <Star aria-hidden />
                Por defecto
              </span>
            )}
            {!r.activa && (
              <span className="chip muted">
                <Archive aria-hidden />
                Dada de baja
              </span>
            )}
            <span className="muted">
              {fmtKm(r.km)} · {fmtDuracionMin(r.duracionMin)} estimadas · {d.paradasTotal} paradas
            </span>
          </div>
        ) : (
          <div className="banner aviso" role="status">
            <TriangleAlert aria-hidden />
            <span>
              <strong>Este viaje no tiene ruta.</strong> La troncal «{d.troncal.nombre}» no tiene rutas en el TMS: no se evalúan desvíos, ETA ni paradas hasta
              rutearla.
            </span>
            <Link className="boton-link" to={`/troncales/${d.troncal.id}`}>
              <Waypoints aria-hidden />
              Rutear la troncal
            </Link>
          </div>
        )}

        {editable && troncal.datos && activas.length > 0 && (
          <div className="cambiar-ruta">
            <label className="field">
              <span>Cambiar la ruta (el viaje todavía no salió)</span>
              <select value={elegida} onChange={(e) => setElegida(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">Elegí una ruta activa de la troncal</option>
                {activas
                  .filter((x) => x.id !== r?.id)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.nombre} · {fmtKm(x.km)}
                      {x.porDefecto ? ' · por defecto' : ''}
                    </option>
                  ))}
              </select>
            </label>
            <button type="button" className="primary" disabled={elegida === '' || guardando} onClick={() => void cambiar()}>
              {guardando ? 'Guardando…' : 'Cambiar ruta'}
            </button>
          </div>
        )}
        {editable && troncal.error && <p className="red nota">{troncal.error}</p>}
        {d.estado === 'PLANIFICADO' && !puedeEditarRutas(usuario?.rol) && <p className="muted nota">Solo despacho y administración pueden cambiar la ruta.</p>}
        {d.estado !== 'PLANIFICADO' && r && <p className="muted nota">La ruta solo se puede cambiar mientras el viaje está planificado.</p>}
      </div>
    </section>
  );
}

export function ViajeDetallePagina() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const valido = Number.isInteger(id) && id > 0;
  const { unidades, alertas, ultimoViaje } = useDatos();
  const { resolver, pedirJustificacion, dialogo } = useAccionesAlerta();
  const ahora = useAhora(30_000);
  const [pestania, setPestania] = useState<Pestania>('detenciones');

  // Se recarga cuando el stream avisa un cambio de este viaje.
  const version = ultimoViaje?.viajeId === id ? ultimoViaje.ts : 0;
  const viaje = useCarga(valido ? () => api.viaje(id) : null, [id, version]);
  const d: ViajeDetalle | null = viaje.datos && viaje.datos.id === id ? viaje.datos : null;
  const activo = d?.estado === 'EN_CURSO' || d?.estado === 'FINALIZADO';
  const recorrido = useCarga(valido && activo ? () => api.recorrido(id, 120) : null, [id, activo, version]);
  const rec = recorrido.datos && recorrido.datos.viajeId === id ? recorrido.datos : null;

  const alertasViaje = useMemo(() => alertas.filter((a) => a.viajeId === id).sort(compararPorNivel), [alertas, id]);
  const abiertasViaje = alertasViaje.filter((a) => a.estado === 'ABIERTA').length;

  const lineas = useMemo<LineaMapa[]>(() => {
    const out: LineaMapa[] = [];
    if (d?.trazaPlanificada) out.push({ id: 'planificada', variante: 'planificada', coords: d.trazaPlanificada.coordinates });
    if (rec?.linea) out.push({ id: 'real', variante: 'real', coords: rec.linea.coordinates });
    return out;
  }, [d, rec]);

  const puntos = useMemo<PuntoMapa[]>(() => {
    if (!d) return [];
    const ps: PuntoMapa[] = d.paradas.map((p) => ({
      id: `p-${p.id}`,
      lat: p.ubicacion.latitud,
      lon: p.ubicacion.longitud,
      variante: `parada-${p.estado}`,
      texto: String(p.orden),
      titulo: `${p.orden}. ${p.nombre} · ${PARADA[p.estado].etiqueta}`,
      detalle: [
        p.llegadaReal ? `llegada ${fmtHora(p.llegadaReal)}` : p.etaActual ? `ETA ${fmtHora(p.etaActual)}` : null,
        p.salidaReal ? `salida ${fmtHora(p.salidaReal)}` : null,
        p.permanenciaMin !== null ? `permanencia ${fmtDuracionMin(p.permanenciaMin)}` : null,
        p.declaradaEn ? `declarada ${fmtHora(p.declaradaEn)}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || 'Sin datos reales todavía',
    }));
    for (const n of d.paradasNoPlanificadas) {
      if (!n.ubicacion) continue;
      ps.push({
        id: `n-${n.id}`,
        lat: n.ubicacion.latitud,
        lon: n.ubicacion.longitud,
        variante: 'noplan',
        texto: '+',
        titulo: `Parada no planificada · ${MOTIVO_NO_PLANIFICADA[n.motivo]}`,
        detalle: `Declarada ${fmtHora(n.fechaHora)}${n.observacion ? ` · «${n.observacion}»` : ''}`,
      });
    }
    // Las detenciones en una parada de la ruta o que confirman una no planificada ya tienen su marcador.
    for (const x of d.detenciones.filter((x) => x.paradaId === null && x.paradaNoPlanificadaId === null)) {
      const t = tipoDetencion(x);
      ps.push({
        id: `d-${x.id}`,
        lat: x.ubicacion.latitud,
        lon: x.ubicacion.longitud,
        variante: t.variante,
        texto: x.fueraDePlan ? '!' : 'O',
        titulo: `Detención · ${t.etiqueta}`,
        detalle: `${fmtHora(x.inicio)} a ${x.fin ? fmtHora(x.fin) : 'en curso'} · ${fmtDuracionMin(x.duracionMin)}`,
      });
    }
    return ps;
  }, [d]);

  const unidadVivo = d?.estado === 'EN_CURSO' ? unidades.find((u) => u.vehiculoIdMicrotrack === d.tractor.idMicrotrack) : undefined;
  const marcadores = useMemo<MarcadorUnidad[]>(
    () =>
      unidadVivo?.vivo
        ? [
            {
              id: unidadVivo.vehiculoIdMicrotrack,
              lat: unidadVivo.vivo.posicion.latitud,
              lon: unidadVivo.vivo.posicion.longitud,
              rumbo: unidadVivo.vivo.rumbo,
              estado: unidadVivo.estado,
              titulo: `${unidadVivo.patente ?? ''} · ${UNIDAD[unidadVivo.estado].etiqueta}`,
              seleccionado: true,
            },
          ]
        : [],
    [unidadVivo],
  );

  // Encuadre inicial: la traza planificada (o las paradas, o la posición de la unidad).
  const encuadre = useMemo<Encuadre | undefined>(() => {
    if (!d) return undefined;
    const pts =
      d.trazaPlanificada?.coordinates ??
      (d.paradas.length
        ? d.paradas.map((p) => [p.ubicacion.longitud, p.ubicacion.latitud] as [number, number])
        : d.vivo
          ? [[d.vivo.posicion.longitud, d.vivo.posicion.latitud] as [number, number]]
          : []);
    return pts.length ? { clave: `${d.id}-${d.ruta?.id ?? 0}`, puntos: pts, zoomMax: 13 } : undefined;
    // Solo al cambiar de viaje o de ruta, no en cada recarga en vivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.id, d?.ruta?.id]);

  if (!valido) return <div className="page"><div className="banner error">El identificador del viaje no es válido.</div></div>;

  return (
    <div className="page">
      <Link to="/viajes" className="back">
        <ArrowLeft aria-hidden size={14} />
        Volver a viajes
      </Link>

      {viaje.error && !d && (
        <div className="banner error" role="alert" style={{ marginTop: 16 }}>
          <TriangleAlert aria-hidden />
          {viaje.error}
          <button type="button" className="btn-sm" onClick={viaje.recargar}>Reintentar</button>
        </div>
      )}
      {!d && viaje.cargando && (
        <div className="skeleton">
          <Loader className="spin" aria-hidden size={16} />
          Cargando viaje…
        </div>
      )}

      {d && (
        <>
          <div className="viaje-head">
            <div>
              <div className="eyebrow">DETALLE DEL VIAJE</div>
              <h1>
                {d.idMara}
                <ChipEstado tipo="viaje" valor={d.estado} />
                {abiertasViaje > 0 && (
                  <span className="chip red">
                    <BellRing aria-hidden />
                    {abiertasViaje} alerta{abiertasViaje > 1 ? 's' : ''} abierta{abiertasViaje > 1 ? 's' : ''}
                  </span>
                )}
              </h1>
              <p className="meta">
                {d.troncal.nombre} · {d.origenNombre ?? '—'} → {d.destinoNombre ?? '—'} · {d.cliente ?? 'Sin cliente'}
              </p>
            </div>
          </div>

          <section className="card" aria-label="Resumen del viaje">
            <div className="body">
              <dl className="facts cols">
                <Dato t="Cliente">{d.cliente ?? '—'}</Dato>
                <Dato t="Empresa">{d.empresa ?? '—'}</Dato>
                <Dato t="Unidad de negocio">
                  {d.unidadNegocio ?? '—'}
                  {d.subunidadNegocio ? ` · ${d.subunidadNegocio}` : ''}
                </Dato>
                <Dato t="Centro de costo">{d.centroCosto ?? '—'}</Dato>
                <Dato t="Troncal">
                  <Link to={`/troncales/${d.troncal.id}`}>{d.troncal.nombre}</Link>
                  <small className="muted"> · {d.troncal.identificacion}</small>
                </Dato>
                <Dato t="Tractor">
                  {d.tractor.patente ?? '—'} · #{d.tractor.interno ?? '—'}
                </Dato>
                <Dato t="Semirremolque">{d.semi ? `${d.semi.patente ?? '—'} · #${d.semi.interno ?? '—'}` : 'Sin semi'}</Dato>
                <Dato t="Choferes">
                  {d.choferes.length ? d.choferes.map((c) => `${c.nombre} (${c.rol === 'PRINCIPAL' ? 'principal' : 'secundario'})`).join(', ') : '—'}
                </Dato>
                <Dato t="Planificado">{fmtFechaHora(d.fechaHoraPlanificada)}</Dato>
                <Dato t="Salida real">{fmtFechaHora(d.salidaReal)}</Dato>
                <Dato t="Llegada real">{fmtFechaHora(d.llegadaReal)}</Dato>
                <Dato t="km GPS">{fmtKm(d.kmGps)}</Dato>
                <Dato t="Odómetro inicial / final">
                  {d.kmInicial === null ? '—' : fmtNum(d.kmInicial)} / {d.kmFinal === null ? '—' : fmtNum(d.kmFinal)}
                </Dato>
                <Dato t="Paradas">
                  {d.paradasTotal === 0 ? (
                    'Sin paradas'
                  ) : (
                    <>
                      {d.paradasCumplidas} cumplidas de {d.paradasTotal}
                      {d.paradasExcedidas > 0 && <span className="amber"> · {d.paradasExcedidas} excedidas</span>}
                      {d.paradasOmitidas > 0 && <span className="red"> · {d.paradasOmitidas} omitidas</span>}
                    </>
                  )}
                </Dato>
                <Dato t="Avance">
                  {fmtPct(d.avance)}
                  <div className="progress big" role="progressbar" aria-label="Avance del viaje" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(d.avance * 100)} style={{ marginTop: 8 }}>
                    <span style={{ width: `${Math.round(d.avance * 100)}%` }} />
                  </div>
                </Dato>
              </dl>
            </div>
          </section>

          <RutaAsignada d={d} onCambiada={viaje.recargar} />

          <div className="viaje-grid">
            <section className="card" aria-label="Línea de tiempo de paradas">
              <header>
                <h2>Paradas · planificado contra real</h2>
                {d.paradasNoPlanificadas.length > 0 && (
                  <span className="list-count">
                    {d.paradasNoPlanificadas.length} no planificada{d.paradasNoPlanificadas.length > 1 ? 's' : ''}
                  </span>
                )}
              </header>
              <LineaTiempoParadas
                paradas={d.paradas}
                noPlanificadas={d.paradasNoPlanificadas}
                vacio={d.ruta ? 'La ruta del viaje no tiene paradas.' : 'Sin ruta no hay paradas planificadas. Cuando el despacho rutee la troncal, el viaje tomará sus paradas.'}
              />
            </section>

            <div>
              <section className="card" aria-label="Mapa del viaje">
                <header>
                  <h2>Recorrido</h2>
                  <span className="list-count">{rec ? `${fmtNum(rec.totalEventos)} eventos` : ''}</span>
                </header>
                <div style={{ position: 'relative' }}>
                  <Mapa className="inline" lineas={lineas} puntos={puntos} unidades={marcadores} encuadre={encuadre} />
                </div>
                <div className="leyenda" aria-label="Referencias del mapa">
                  <span><b className="route-line" style={{ display: 'inline-block' }} /> Recorrido real</span>
                  <span><b className="route-line planned" style={{ display: 'inline-block' }} /> Traza de la ruta</span>
                  <span><i style={{ background: 'var(--brand)', borderRadius: '50%' }} /> Parada cumplida</span>
                  <span><i style={{ background: 'var(--amber)', borderRadius: '50%' }} /> Excedida</span>
                  <span><i style={{ background: 'var(--red)', borderRadius: '50%' }} /> Omitida</span>
                  <span><i className="ley-np" /> No planificada (+)</span>
                  <span><i style={{ background: 'var(--orange)' }} /> Detención fuera de plan (!)</span>
                </div>
              </section>

              <section className="card" aria-label="Detalle del viaje">
                <div className="tabs" role="tablist">
                  {(
                    [
                      ['detenciones', 'Detenciones', d.detenciones.length],
                      ['alertas', 'Alertas', alertasViaje.length],
                      ['avisos', 'Avisos de Mara', d.avisosMara.length],
                      ['eventos', 'Eventos', d.hitos.length],
                    ] as [Pestania, string, number][]
                  ).map(([k, t, n]) => (
                    <button key={k} type="button" role="tab" aria-selected={pestania === k} className={pestania === k ? 'active' : ''} onClick={() => setPestania(k)}>
                      {t} <span className="count">{n}</span>
                    </button>
                  ))}
                </div>

                {pestania === 'detenciones' &&
                  (d.detenciones.length === 0 ? (
                    <div className="vacio">Todavía no hay detenciones registradas.</div>
                  ) : (
                    <div className="table-wrap">
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Parada</th>
                            <th>Inicio</th>
                            <th>Fin</th>
                            <th className="num">Duración</th>
                            <th>Ubicación</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.detenciones.map((x) => {
                            const t = tipoDetencion(x);
                            const Icono = t.icono;
                            const parada = d.paradas.find((p) => p.id === x.paradaId);
                            const np = d.paradasNoPlanificadas.find((n) => n.id === x.paradaNoPlanificadaId);
                            return (
                              <tr key={x.id}>
                                <td>
                                  <span className={`chip ${t.color}`}>
                                    <Icono aria-hidden />
                                    {t.etiqueta}
                                  </span>
                                </td>
                                <td>{parada ? `${parada.orden}. ${parada.nombre}` : np ? `Declarada: ${MOTIVO_NO_PLANIFICADA[np.motivo].toLocaleLowerCase('es')}` : '—'}</td>
                                <td>{fmtFechaHora(x.inicio)}</td>
                                <td>{x.fin ? fmtFechaHora(x.fin) : <span className="amber">En curso</span>}</td>
                                <td className="num">{fmtDuracionMin(x.duracionMin)}</td>
                                <td>{fmtCoordenadas(x.ubicacion.latitud, x.ubicacion.longitud)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}

                {pestania === 'alertas' &&
                  (alertasViaje.length === 0 ? (
                    <div className="vacio">
                      <BellOff aria-hidden />
                      Este viaje no tiene alertas.
                    </div>
                  ) : (
                    alertasViaje.map((a) => <FilaAlerta key={a.id} alerta={a} ahora={ahora} onResolver={(x) => void resolver(x)} onJustificar={pedirJustificacion} />)
                  ))}

                {pestania === 'avisos' &&
                  (d.avisosMara.length === 0 ? (
                    <div className="vacio">
                      <Inbox aria-hidden />
                      Mara todavía no envió avisos de este viaje (inicio, paradas o fin).
                    </div>
                  ) : (
                    <div className="table-wrap">
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>Aviso</th>
                            <th>Hora del hecho</th>
                            <th>Recibido</th>
                            <th>Detalle</th>
                            <th>Posición del teléfono</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...d.avisosMara]
                            .sort((a, b) => a.fechaHora.localeCompare(b.fechaHora))
                            .map((a) => {
                              const { etiqueta, icono: Icono } = ETIQUETA_AVISO[a.tipo];
                              const pos = a.cuerpo.posicion;
                              return (
                                <tr key={a.id}>
                                  <td>
                                    <strong style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                      <Icono aria-hidden size={14} />
                                      {etiqueta}
                                    </strong>
                                    <small title="Identificador del aviso en Mara (idempotencia)">{a.eventoId}</small>
                                  </td>
                                  <td>{fmtFechaHora(a.fechaHora)}</td>
                                  <td>{fmtFechaHora(a.recibidoEn)}</td>
                                  <td style={{ maxWidth: 320 }}>{describirAviso(a, d)}</td>
                                  <td>{pos ? fmtCoordenadas(pos.latitud, pos.longitud) : <span className="muted">Sin posición (se usa la de Microtrack)</span>}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  ))}

                {pestania === 'eventos' && (
                  <>
                    <div className="body">
                      <div className="sub-h">Hitos del viaje</div>
                      <ol className="check-list">
                        {d.hitos.map((h, i) => {
                          const Icono = ICONO_HITO[h.tipo];
                          return (
                            <li key={`${h.fechaHora}-${i}`}>
                              <Icono aria-hidden />
                              <span>
                                <strong>{ETIQUETA_HITO[h.tipo]}</strong> · {h.descripcion}
                                <small>{fmtFechaHora(h.fechaHora)}</small>
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                      <div className="sub-h" style={{ marginTop: 22 }}>Puntos del recorrido {rec ? `(${fmtNum(rec.puntos.length)} de ${fmtNum(rec.totalEventos)})` : ''}</div>
                    </div>
                    {!rec || rec.puntos.length === 0 ? (
                      <div className="vacio">{recorrido.cargando ? 'Cargando recorrido…' : 'Sin puntos de recorrido.'}</div>
                    ) : (
                      <div className="table-wrap" style={{ maxHeight: 320 }}>
                        <table className="tabla">
                          <thead>
                            <tr>
                              <th>Hora</th>
                              <th>Evento</th>
                              <th className="num">Velocidad</th>
                              <th className="num">Rumbo</th>
                              <th>Posición</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rec.puntos.map((p, i) => (
                              <tr key={i}>
                                <td>{fmtFechaHora(p.fechaHora)}</td>
                                <td>{p.tipoEvento}</td>
                                <td className="num">{p.velocidadKmh === null ? '—' : `${fmtNum(p.velocidadKmh)} km/h`}</td>
                                <td className="num">{p.rumbo === null ? '—' : `${fmtNum(p.rumbo)}°`}</td>
                                <td>{fmtCoordenadas(p.latitud, p.longitud)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </section>
            </div>
          </div>
        </>
      )}
      {dialogo}
    </div>
  );
}
